import { useState } from "react";
import { Button, ButtonSize } from "components/Button";
import { ButtonColor } from "components/Button";
import { ButtonFill } from "components/Button";
import { Select } from "components/Select";
import { StudentsService, TutorsService, ParentsService } from "client/openapi";
import { InviteType } from "client/openapi/models/InviteType";
import { useAuth0 } from "@auth0/auth0-react";
import { useContext } from "react";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import Notifications from "util/notifications";
import { ArrowLeftIcon } from "@radix-ui/react-icons";

type UnknownContactDialogProps = {
  inboundContactInfo: {
    email?: string | null;
    phone_number?: string | null;
  };
  onClose: () => void;
  organization: {
    id: number;
    students: Array<{
      id: number;
      first_name: string;
      last_name: string;
    }>;
    tutors: Array<{
      id: number;
      first_name: string;
      last_name: string;
    }>;
    parents: Array<{
      id: number;
      first_name: string;
      last_name: string;
    }>;
  };
};

type ActionType = "link" | "create";
type UserType = "student" | "tutor" | "parent";

export default function UnknownContactDialog({
  inboundContactInfo,
  onClose,
  organization,
}: UnknownContactDialogProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [actionType, setActionType] = useState<ActionType | null>(null);
  const [userType, setUserType] = useState<UserType | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { user } = useAuth0();
  const { currently_selected_organization } = useContext(
    OrgRolesAndAccountContext
  );

  console.log(selectedUserId, "selectedUserId", [selectedUserId]);
  const handleActionSelect = (action: ActionType) => {
    setActionType(action);
    setStep(2);
  };

  const handleUserTypeSelect = (type: UserType) => {
    setUserType(type);
    if (actionType === "link") {
      setStep(3);
    } else {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    if (!user?.sub || !currently_selected_organization) return;

    setIsLoading(true);
    try {
      if (actionType === "link" && selectedUserId) {
        // Update existing user
        const updateData = {
          ...(inboundContactInfo.email && { email: inboundContactInfo.email }),
          ...(inboundContactInfo.phone_number && {
            phone_number: inboundContactInfo.phone_number,
          }),
        };

        switch (userType) {
          case "student":
            await StudentsService.updateStudent({
              studentId: selectedUserId,
              requestBody: updateData,
            });
            break;
          case "tutor":
            await TutorsService.updateTutor({
              tutorId: selectedUserId,
              requestBody: updateData,
            });
            break;
          case "parent":
            await ParentsService.updateParent({
              parentId: selectedUserId,
              requestBody: updateData,
            });
            break;
        }
      } else if (actionType === "create") {
        // Create new user
        const inviteData = {
          first_name: firstName,
          last_name: lastName,
          type:
            userType === "student"
              ? InviteType.STUDENT
              : userType === "tutor"
              ? InviteType.TUTOR
              : InviteType.PARENT,
          org_invited_by: currently_selected_organization,
          acct_invited_by: user.sub,
          ...(inboundContactInfo.email && { email: inboundContactInfo.email }),
          ...(inboundContactInfo.phone_number && {
            phone_number: inboundContactInfo.phone_number,
          }),
        };

        switch (userType) {
          case "student":
            await StudentsService.inviteStudentToOrg({
              requestBody: inviteData,
              createAccount: false,
            });
            break;
          case "tutor":
            await TutorsService.inviteTutor({
              requestBody: inviteData,
              createAccount: false,
            });
            break;
          case "parent":
            await ParentsService.inviteParent({
              requestBody: inviteData,
              createAccount: false,
            });
            break;
        }
      }

      Notifications.success("Successfully processed contact information");
      onClose();
    } catch (error: any) {
      Notifications.error(error.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">
        What would you like to do with this contact?
      </h3>
      <div className="flex gap-4">
        <Button
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
          onClick={() => handleActionSelect("link")}
        >
          Link to Existing User
        </Button>
        <Button
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
          onClick={() => handleActionSelect("create")}
        >
          Create New User
        </Button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Select User Type</h3>
      <div className="flex gap-4">
        <Button
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
          onClick={() => handleUserTypeSelect("student")}
        >
          Student
        </Button>
        <Button
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
          onClick={() => handleUserTypeSelect("tutor")}
        >
          Tutor
        </Button>
        <Button
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
          onClick={() => handleUserTypeSelect("parent")}
        >
          Parent
        </Button>
      </div>
      <div className="flex justify-start w-full gap-2">
        <Button
          color={ButtonColor.BLACK}
          fill={ButtonFill.NO_BORDER}
          onClick={() => {
            setStep((step - 1) as 1 | 2 | 3);
          }}
          size={ButtonSize.SMALL}
        >
          <ArrowLeftIcon /> Back
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => {
    if (actionType === "link") {
      const users =
        userType === "student"
          ? organization.students
          : userType === "tutor"
          ? organization.tutors
          : organization.parents;

      return (
        <div className="space-y-4">
          <h3 className="text-lg font-medium">
            Select {userType!.charAt(0).toUpperCase() + userType!.slice(1)}
          </h3>
          <Select
            value={users
              .find((user) => user.id === selectedUserId)
              ?.id.toString()}
            onChange={(user) => setSelectedUserId(user.value)}
            options={users.map((user) => ({
              value: user.id.toString(),
              label: `${user.first_name} ${user.last_name}`,
            }))}
          />
          <div className="flex justify-between w-full gap-2">
            <Button
              color={ButtonColor.BLACK}
              fill={ButtonFill.NO_BORDER}
              onClick={() => {
                setStep((step - 1) as 1 | 2 | 3);
              }}
              size={ButtonSize.SMALL}
            >
              <ArrowLeftIcon /> Back
            </Button>
            <Button
              color={ButtonColor.DARK_BLUE}
              fill={ButtonFill.HOLLOW}
              onClick={handleSubmit}
              disabled={!selectedUserId || isLoading}
            >
              {isLoading ? "Processing..." : "Link Contact"}
            </Button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-medium">
            Create New {userType!.charAt(0).toUpperCase() + userType!.slice(1)}
          </h3>
          <div className="space-y-2">
            <input
              className="input"
              placeholder="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
            <input
              className="input"
              placeholder="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
          <div className="flex justify-between w-full gap-2">
            <Button
              color={ButtonColor.BLACK}
              fill={ButtonFill.NO_BORDER}
              onClick={() => {
                setStep((step - 1) as 1 | 2 | 3);
              }}
              size={ButtonSize.SMALL}
            >
              <ArrowLeftIcon /> Back
            </Button>
            <Button
              color={ButtonColor.DARK_BLUE}
              fill={ButtonFill.HOLLOW}
              onClick={handleSubmit}
              disabled={!firstName || !lastName || isLoading}
            >
              {isLoading ? "Processing..." : "Create User"}
            </Button>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="mt-4">
      <div className="my-6 p-4 bg-gray-200 rounded-lg">
        <h4 className="text-sm font-medium text-gray-500 mb-1">
          Contact Information
        </h4>
        {inboundContactInfo.email && (
          <p className="text-base">{inboundContactInfo.email}</p>
        )}
        {inboundContactInfo.phone_number && (
          <p className="text-base">{inboundContactInfo.phone_number}</p>
        )}
      </div>
      {step === 1 && renderStep1()}
      {step === 2 && renderStep2()}
      {step === 3 && renderStep3()}
    </div>
  );
}
